/**
 * Copia texto para o clipboard do usuário.
 *
 * **Dois caminhos por necessidade prática**:
 *   1. `navigator.clipboard.writeText` — API moderna, mas exige
 *      **secure context** (HTTPS, localhost, file://). Falha em
 *      HTTP com host custom (ex: `kmgestao.test:5174` usado em dev
 *      local com /etc/hosts).
 *   2. Fallback `document.execCommand('copy')` via textarea
 *      temporária — deprecated mas continua suportado em todos os
 *      browsers atuais. Não requer secure context.
 *
 * Retorna `true` em sucesso, `false` caso contrário (consumer mostra
 * toast ou orienta o user a copiar manualmente).
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Tenta a API moderna primeiro quando disponível.
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard?.writeText &&
    window.isSecureContext
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Segue para o fallback — alguns navegadores/combinações
      // rejeitam mesmo em secure context (permissão negada pelo user).
    }
  }

  // Fallback via textarea + execCommand. Técnica clássica: criar um
  // elemento off-screen, selecionar, copiar, remover.
  if (typeof document === 'undefined') return false;
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.left = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
